import { getUser } from '@/features/Auth/auth.hooks'
import { createDelegateSuggestionsQueryOptions } from '@/features/Cards/queries'
import { useQuery } from '@tanstack/react-query'
import { useModal } from '@/features/Modal/Modal.hooks'
import { DrawerModalListItem } from '@/features/Modal/Modal.types'
import { DrawerModalList } from '@/features/Modal/ui/DrawerModalList/DrawerModalList'
import { CryptoBalance } from '@/features/Wallets/wallet.types'
import { AppRoutes } from '@/routes'
import { getUrl } from '@/shared/helpers/url'
import { formatTokenAmount } from '@/shared/helpers/crypto'
import { useBalancesStore } from '@/store'
import { useTransactionsStore } from '@/store/transactions/transactions'
import { useRouter } from 'nextjs-toploader/app'
import React, { useCallback, useMemo } from 'react'

type Props = {
  cryptoBalance: CryptoBalance;
  tokenAddress: string;
}

export function CardSpendableUpdateModal({ tokenAddress, cryptoBalance }: Props) {
  const router = useRouter()
  const { destroyModal } = useModal()
  const user = getUser()
  const { data, isLoading, isSuccess } = useQuery(createDelegateSuggestionsQueryOptions(tokenAddress, !!user))
  const setTransaction = useTransactionsStore((state) => state.setTransaction)
  const selectedWallet = useBalancesStore((state) => state.selectedWallet)

  const suggestions = useMemo(() => ((isSuccess && cryptoBalance) ? data.map((sugg: any) => ({
    text: `Set ${formatTokenAmount({ ...cryptoBalance, amount: sugg.amount })} ${cryptoBalance.symbol}`,
    icon: null,
    visible: true,
    onClick: () => {
      setTransaction({
        cryptoBalance: {
          ...cryptoBalance,
          amount: sugg.amount,
        },
        type: 'set-spendable-balance',
        wallet: selectedWallet.address,
        redirectLink: AppRoutes.CARDS_PAGE,
      })
      router.push(getUrl(AppRoutes.TRANSACTION_CONFIRM, { type: 'set-spendable-balance' }))
      destroyModal()
    },
  })) : []), [isSuccess, data, setTransaction, selectedWallet, router, destroyModal, cryptoBalance])

  const handlePush = useCallback((link: string) => {
    router.push(link)
    destroyModal()
  }, [destroyModal, router])

  const handleReset = useCallback(() => {
    setTransaction({
      cryptoBalance: {
        ...cryptoBalance,
        amount: '0',
      },
      type: 'revoke-spendable-balance',
      wallet: selectedWallet.address,
    })
    router.push(getUrl(AppRoutes.TRANSACTION_CONFIRM, { type: 'revoke-spendable-balance' }))
    destroyModal()
  }, [cryptoBalance, destroyModal, router, selectedWallet.address, setTransaction])

  const spendableActionsList: DrawerModalListItem[] = [
    ...suggestions,
    {
      text: 'Enter manually',
      icon: null,
      visible: true,
      onClick: () => handlePush(`/assets/${tokenAddress}/balance`),
    },
    {
      text: 'Reset Spendable balance',
      icon: null,
      visible: true,
      isRemove: true,
      onClick: handleReset,
    },
  ]

  return (
    <DrawerModalList
      isLoading={isLoading || !selectedWallet}
      list={spendableActionsList}
      title="Spendable balance"
      description={
        <>
          This is the balance you have entrusted for spending in the GoDefi app. Important, the balance is not updated, once it is exhausted, you need to specify and apply the approved amount for spending again.
          <a href="#">More about the Spendable balance in our FAQ.</a>
        </>
      }
    />
  )
}
